import React, { useEffect, useRef } from "react";
import { useTheme } from "../theme-provider";

const getViewportSize = () => {
  const vv = window.visualViewport;
  return {
    width: vv?.width ?? window.innerWidth,
    height: vv?.height ?? window.innerHeight,
  };
};

const getConfig = (width) => {
  if (width < 480) {
    return {
      particleCount: 28,
      connectionDistance: 95,
      maxSpeed: 0.38,
      minSpeed: 0.16,
      orbScale: 0.55,
      lineOpacity: 0.18,
      nodeSize: 1.2,
    };
  }
  if (width < 768) {
    return {
      particleCount: 40,
      connectionDistance: 115,
      maxSpeed: 0.42,
      minSpeed: 0.17,
      orbScale: 0.7,
      lineOpacity: 0.2,
      nodeSize: 1.4,
    };
  }
  if (width < 1024) {
    return {
      particleCount: 55,
      connectionDistance: 130,
      maxSpeed: 0.45,
      minSpeed: 0.18,
      orbScale: 0.85,
      lineOpacity: 0.22,
      nodeSize: 1.5,
    };
  }
  return {
    particleCount: 70,
    connectionDistance: 140,
    maxSpeed: 0.45,
    minSpeed: 0.18,
    orbScale: 1,
    lineOpacity: 0.22,
    nodeSize: 1.5,
  };
};

const AnimatedBackground = () => {
  const canvasRef = useRef(null);
  const { theme } = useTheme();
  const themeRef = useRef(theme);

  useEffect(() => {
    themeRef.current = theme;
  }, [theme]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d", { alpha: true });
    let animationFrameId;
    let particles = [];
    let orbs = [];
    let width = 0;
    let height = 0;
    let tick = 0;
    let config = getConfig(window.innerWidth);
    let reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const mouse = { x: null, y: null };

    const isDarkMode = () =>
      themeRef.current === "dark" ||
      (themeRef.current === "system" &&
        window.matchMedia("(prefers-color-scheme: dark)").matches) ||
      document.documentElement.classList.contains("dark");

    const palettes = {
      light: {
        bg: ["#f5f9f7", "#f8f6ef", "#faf8f0"],
        nodes: [
          "rgba(13, 72, 51, 0.45)",
          "rgba(20, 100, 70, 0.4)",
          "rgba(221, 168, 53, 0.42)",
        ],
        orbs: [
          "rgba(13, 72, 51, 0.14)",
          "rgba(20, 100, 70, 0.1)",
          "rgba(221, 168, 53, 0.12)",
        ],
        lines: "13, 72, 51",
      },
      dark: {
        bg: ["#0c0e0d", "#101211", "#131615"],
        nodes: [
          "rgba(221, 168, 53, 0.35)",
          "rgba(13, 72, 51, 0.45)",
          "rgba(180, 180, 180, 0.2)",
        ],
        orbs: [
          "rgba(13, 72, 51, 0.08)",
          "rgba(221, 168, 53, 0.06)",
          "rgba(255, 255, 255, 0.03)",
        ],
        lines: "221, 168, 53",
      },
    };

    class Particle {
      constructor(w, h) {
        this.x = Math.random() * w;
        this.y = Math.random() * h;
        const angle = Math.random() * Math.PI * 2;
        const speed = config.minSpeed + Math.random() * (config.maxSpeed - config.minSpeed);
        this.vx = Math.cos(angle) * speed;
        this.vy = Math.sin(angle) * speed;
        this.baseSize = Math.random() * config.nodeSize + config.nodeSize;
        this.colorIndex = Math.floor(Math.random() * 3);
        this.pulseOffset = Math.random() * Math.PI * 2;
      }

      update(w, h) {
        this.x += this.vx;
        this.y += this.vy;

        if (this.x < -20) this.x = w + 20;
        else if (this.x > w + 20) this.x = -20;
        if (this.y < -20) this.y = h + 20;
        else if (this.y > h + 20) this.y = -20;

        const speed = Math.hypot(this.vx, this.vy);
        if (speed < config.minSpeed * 0.8) {
          const angle = Math.random() * Math.PI * 2;
          this.vx = Math.cos(angle) * config.minSpeed;
          this.vy = Math.sin(angle) * config.minSpeed;
        } else if (speed > config.maxSpeed) {
          this.vx = (this.vx / speed) * config.maxSpeed;
          this.vy = (this.vy / speed) * config.maxSpeed;
        }

        if (mouse.x != null) {
          const dx = this.x - mouse.x;
          const dy = this.y - mouse.y;
          const dist = Math.hypot(dx, dy);
          const reach = width < 768 ? 80 : 100;
          if (dist < reach && dist > 0) {
            const force = ((reach - dist) / reach) * 0.06;
            this.vx += (dx / dist) * force;
            this.vy += (dy / dist) * force;
          }
        }
      }

      draw(colors, t) {
        const pulse = reducedMotion ? 1 : 1 + Math.sin(t * 0.03 + this.pulseOffset) * 0.2;
        const size = this.baseSize * pulse;

        ctx.beginPath();
        ctx.arc(this.x, this.y, size, 0, Math.PI * 2);
        ctx.fillStyle = colors[this.colorIndex];
        ctx.fill();

        ctx.beginPath();
        ctx.arc(this.x, this.y, size * 2.2, 0, Math.PI * 2);
        ctx.fillStyle = colors[this.colorIndex].replace(/[\d.]+\)$/, "0.07)");
        ctx.fill();
      }
    }

    const initOrbs = (w, h) => {
      const base = Math.min(w, h);
      orbs = [
        { x: w * 0.12, y: h * 0.18, r: base * 0.32 * config.orbScale, dx: 0.2, dy: 0.12, colorIndex: 0 },
        { x: w * 0.88, y: h * 0.22, r: base * 0.28 * config.orbScale, dx: -0.15, dy: 0.18, colorIndex: 1 },
        { x: w * 0.55, y: h * 0.78, r: base * 0.36 * config.orbScale, dx: 0.12, dy: -0.14, colorIndex: 2 },
      ];
    };

    const initParticles = (w, h) => {
      particles = Array.from({ length: config.particleCount }, () => new Particle(w, h));
    };

    const setSize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, width < 768 ? 1.5 : 2);
      const vp = getViewportSize();
      width = vp.width;
      height = vp.height;
      config = getConfig(width);

      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      initParticles(width, height);
      initOrbs(width, height);
    };

    const drawBackground = (palette) => {
      const gradient = ctx.createLinearGradient(0, 0, width, height * 0.8);
      gradient.addColorStop(0, palette.bg[0]);
      gradient.addColorStop(0.5, palette.bg[1]);
      gradient.addColorStop(1, palette.bg[2]);
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
    };

    const drawOrbs = (palette) => {
      if (reducedMotion) return;

      orbs.forEach((orb, i) => {
        orb.x += orb.dx;
        orb.y += orb.dy;
        if (orb.x < width * 0.05 || orb.x > width * 0.95) orb.dx *= -1;
        if (orb.y < height * 0.05 || orb.y > height * 0.95) orb.dy *= -1;

        const pulse = 1 + Math.sin(tick * 0.015 + i * 1.2) * 0.08;
        const radius = orb.r * pulse;
        const g = ctx.createRadialGradient(orb.x, orb.y, 0, orb.x, orb.y, radius);
        g.addColorStop(0, palette.orbs[orb.colorIndex]);
        g.addColorStop(0.65, palette.orbs[orb.colorIndex].replace(/[\d.]+\)$/, "0.03)"));
        g.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(orb.x, orb.y, radius, 0, Math.PI * 2);
        ctx.fill();
      });
    };

    const drawConnections = (lineColor) => {
      const maxDistSq = config.connectionDistance * config.connectionDistance;

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];

        for (let j = i + 1; j < particles.length; j++) {
          const q = particles[j];
          const dx = p.x - q.x;
          const dy = p.y - q.y;
          const distSq = dx * dx + dy * dy;

          if (distSq < maxDistSq) {
            const distance = Math.sqrt(distSq);
            const opacity = (1 - distance / config.connectionDistance) * config.lineOpacity;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(q.x, q.y);
            ctx.strokeStyle = `rgba(${lineColor}, ${opacity})`;
            ctx.lineWidth = width < 768 ? 0.6 : 0.8;
            ctx.stroke();
          }
        }

        if (mouse.x != null) {
          const dx = p.x - mouse.x;
          const dy = p.y - mouse.y;
          const dist = Math.hypot(dx, dy);
          const reach = width < 768 ? 120 : 150;
          if (dist < reach) {
            const opacity = (1 - dist / reach) * 0.28;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(mouse.x, mouse.y);
            ctx.strokeStyle = `rgba(${lineColor}, ${opacity})`;
            ctx.lineWidth = 0.8;
            ctx.stroke();
          }
        }
      }
    };

    const animate = () => {
      if (!ctx) return;
      tick += 1;
      const palette = isDarkMode() ? palettes.dark : palettes.light;

      drawBackground(palette);
      drawOrbs(palette);

      particles.forEach((p) => {
        p.update(width, height);
        p.draw(palette.nodes, tick);
      });

      drawConnections(palette.lines);
      animationFrameId = requestAnimationFrame(animate);
    };

    let resizeTimer;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(setSize, 100);
    };

    const handlePointer = (x, y) => {
      mouse.x = x;
      mouse.y = y;
    };
    const handleMouseMove = (e) => handlePointer(e.clientX, e.clientY);
    const handleTouchMove = (e) => {
      if (e.touches[0]) handlePointer(e.touches[0].clientX, e.touches[0].clientY);
    };
    const handlePointerLeave = () => {
      mouse.x = null;
      mouse.y = null;
    };

    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handleMotionChange = (e) => {
      reducedMotion = e.matches;
    };

    setSize();
    animate();

    window.addEventListener("resize", handleResize);
    window.visualViewport?.addEventListener("resize", handleResize);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseout", handlePointerLeave);
    window.addEventListener("touchmove", handleTouchMove, { passive: true });
    window.addEventListener("touchend", handlePointerLeave);
    motionQuery.addEventListener("change", handleMotionChange);

    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener("resize", handleResize);
      window.visualViewport?.removeEventListener("resize", handleResize);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseout", handlePointerLeave);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handlePointerLeave);
      motionQuery.removeEventListener("change", handleMotionChange);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="fixed inset-0 w-full h-full z-0 pointer-events-none touch-none"
      style={{ height: "100dvh", width: "100vw" }}
    />
  );
};

export default AnimatedBackground;
